import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Tone = 'info' | 'success' | 'warning' | 'danger';

/*
 * Yalnızca SOL ŞERİT ve SİMGE renkli; başlık ve gövde normal metin rengiyle.
 *
 * Eskiden bandın tüm metni ton rengindeydi (`text-info` gibi). Renkli metin
 * yumuşak zemin üzerinde kontrastı düşürüyor ve uzun bir açıklamayı okumayı
 * zorlaştırıyor. Renk, DİKKAT ÇEKMEK için gerekli; METNİ TAŞIMAK için değil.
 */
const TONE_STYLE: Record<Tone, { wrapper: string; mark: string; icon: typeof Info }> = {
  info: { wrapper: 'bg-info-soft border-info/25', mark: 'text-info', icon: Info },
  success: { wrapper: 'bg-success-soft border-success/25', mark: 'text-success', icon: CheckCircle2 },
  warning: { wrapper: 'bg-warning-soft border-warning/25', mark: 'text-warning', icon: AlertTriangle },
  danger: { wrapper: 'bg-danger-soft border-danger/25', mark: 'text-danger', icon: XCircle },
};

/**
 * §19.2 — Hata bandı: "ne oldu / ne yapmalıyım / tekrar dene".
 * Bu yüzden `action` alanı var: kullanıcı çıkmaz sokakta bırakılmaz.
 */
export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: Tone;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { wrapper, mark, icon: Icon } = TONE_STYLE[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex gap-2.5 rounded-[var(--radius-sm)] border border-l-[3px] px-3.5 py-3 text-fg',
        wrapper,
        className,
      )}
    >
      <Icon className={cn('mt-px size-4 shrink-0', mark)} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-[0.8125rem] font-semibold leading-5">{title}</p>
        {children && <div className="text-xs leading-relaxed text-fg-muted">{children}</div>}
        {action && <div className="pt-1.5">{action}</div>}
      </div>
    </div>
  );
}
